import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Globe,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import type { ScrapedWebsite } from '@proptr/shared';
import { client } from '../../main.js';
import { setTestMode } from '../../lib/testMode.js';
import { ProptreePreview } from '../ProptreePreview.js';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import {
  OnboardingShell,
  type OnboardingStepMeta,
} from './OnboardingShell.js';

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; cached: boolean }
  | { kind: 'error'; message: string };

// Where the left-hand preview gets its listings and team from.
//   resolving — first /api/me/demo-data call in flight
//   pending   — the signup import is still running; wait for it
//   ready     — this brokerage's own set is on file, preview with `e`
//   failed    — we owed this brokerage their own properties and could not
//               get them. Say so, and send them into the live app rather
//               than Testing mode; a fixture here would be claiming we
//               imported data we never fetched.
//   static    — no agency was picked at signup, so nothing was promised.
//               The fabricated seed set is an honest preview.
type DemoState =
  | { kind: 'resolving' }
  | { kind: 'pending' }
  | { kind: 'ready'; enterprise: string }
  | { kind: 'failed' }
  | { kind: 'static' };

const DEMO_POLL_MS = 4_000;

/**
 * How long to wait on a 'pending' import before treating it as static.
 * The import itself takes 30-60s. A job that dies without recording a
 * failure stays 'running' in the DB, and the API's own staleness window
 * is measured in minutes — this is the user-facing ceiling, so a dead
 * job costs a short wait rather than a preview that never arrives.
 */
const DEMO_WAIT_MS = 2 * 60 * 1_000;

// `ScrapedWebsite.fetchedAt` is `string | Date` on the wire. `normalize()`
// always coerces to `Date`, so the working draft uses this narrowed type.
// `mainColor` / `secondaryColor` are derived from `colors[0..1]` on first
// render but become user-editable from then on.
type DraftScrape = Omit<ScrapedWebsite, 'fetchedAt'> & {
  fetchedAt: Date;
  mainColor: string;
  secondaryColor: string;
};

interface Props {
  steps: ReadonlyArray<OnboardingStepMeta>;
  stepId: string;
  fallbackWebsite: string | null;
  onContinue: () => void;
}

const EMPTY: DraftScrape = {
  sourceUrl: '',
  companyName: null,
  logo: null,
  favicon: null,
  description: null,
  colors: [],
  mainColor: '',
  secondaryColor: '',
  email: null,
  phone: null,
  locations: [],
  services: [],
  fetchedAt: new Date(0),
};

export function WebsiteAnalysisStep({
  steps,
  stepId,
  fallbackWebsite,
  onContinue,
}: Props) {
  const { user } = useAuth();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [draft, setDraft] = useState<DraftScrape>(EMPTY);
  // Iframe URL for the demo preview, set ONCE from the first scrape
  // result so the demo page server-renders with the scraped branding on
  // its very first paint (no placeholder flash). Live edits afterwards
  // flow through the postMessage patch, not URL changes — deriving this
  // from the live draft would reload the iframe on every keystroke.
  const [demoSlug, setDemoSlug] = useState<string | null>(null);
  // Per-field validation errors. Set when Continue is clicked with empty
  // required fields; cleared automatically when the user types.
  const [errors, setErrors] = useState<{ email?: string; phone?: string }>({});
  // Continue-click save state — the draft persists to the enterprise via
  // /api/onboarding/save-brand before the wizard advances.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Whether the preview shows this brokerage's imported listings/team or
  // the fabricated seed set. Resolved in parallel with the site scrape.
  const [demo, setDemo] = useState<DemoState>({ kind: 'resolving' });
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void runScrape({ force: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while the signup import is still running. Every exit that isn't
  // a finished import — error, timeout, no agency picked — lands on
  // 'static', so the preview always has something to render and the user
  // never sees a failure they can't act on.
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const deadline = Date.now() + DEMO_WAIT_MS;

    async function poll(): Promise<void> {
      try {
        const res = await client.me.demoData();
        if (cancelled) return;
        if (res.source === 'agency' && res.enterprise) {
          setDemo({ kind: 'ready', enterprise: res.enterprise });
          return;
        }
        if (res.source === 'pending' && Date.now() < deadline) {
          setDemo({ kind: 'pending' });
          timer = window.setTimeout(() => void poll(), DEMO_POLL_MS);
          return;
        }
        if (res.source === 'failed') {
          setDemo({ kind: 'failed' });
          return;
        }
        if (res.source === 'pending') {
          // Past our own ceiling with the import still unresolved. Treat a
          // wait that outlived the deadline as a failure, not as "no import
          // was ever promised" — the user picked an agency and is owed one.
          setDemo({ kind: 'failed' });
          return;
        }
      } catch {
        // API down or not signed in — nothing was promised we can verify.
      }
      if (!cancelled) setDemo({ kind: 'static' });
    }

    void poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  async function runScrape({ force }: { force: boolean }) {
    setStatus({ kind: 'loading' });
    try {
      const res = await client.onboarding.scrapeWebsite({ force });
      const norm = normalize(res.scrape, user?.email ?? null);
      setDraft(norm);
      setDemoSlug((prev) => prev ?? buildDemoSlug(norm));
      setStatus({ kind: 'ready', cached: res.cached });
    } catch (err) {
      // Scrape failed — still show the demo preview, just unseeded.
      setDemoSlug((prev) => prev ?? DEMO_PROPTREE_SLUG);
      setStatus({
        kind: 'error',
        message:
          (err as Error).message ||
          "Couldn't read your website. Try again, or skip and we'll fill this in by hand.",
      });
    }
  }

  function update<K extends keyof DraftScrape>(
    key: K,
    value: DraftScrape[K],
  ) {
    setDraft((d) => ({ ...d, [key]: value }));
    // Clear the matching field error so the user gets immediate feedback
    // that they've fixed it without having to re-submit.
    if (key === 'email' || key === 'phone') {
      const errKey = key as 'email' | 'phone';
      setErrors((prev) => {
        if (!prev[errKey]) return prev;
        const { [errKey]: _drop, ...rest } = prev;
        return rest;
      });
    }
  }

  function validate(): boolean {
    const next: typeof errors = {};
    const email = (draft.email ?? '').trim();
    const phone = (draft.phone ?? '').trim();
    if (!email) {
      next.email = 'Required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = 'Enter a valid email.';
    }
    if (!phone) next.phone = 'Required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  return (
    <OnboardingShell
      steps={steps}
      currentStepId={stepId}
      illustration={
        <Illustration
          status={status}
          website={fallbackWebsite}
          scrape={draft}
          demoSlug={previewSlug(demoSlug, demo)}
          demoPending={demo.kind === 'pending'}
          demoFailed={demo.kind === 'failed'}
        />
      }
    >
      <>
        <h1>
          {status.kind === 'loading'
            ? 'Reading your site'
            : status.kind === 'error'
              ? 'We hit a snag'
              : 'Confirm the basics'}
        </h1>
        <p className="auth-subtitle">
          {status.kind === 'loading' &&
            `Scanning ${prettyHost(fallbackWebsite)} for your logo, brand colours and contact details — usually 10–20 seconds.`}
          {status.kind === 'error' && status.message}
          {status.kind === 'ready' &&
            "We extracted what we could — edit anything that's off."}
        </p>

        {status.kind === 'error' ? (
          <div className="space-y-4">
            <div className="auth-banner" role="alert">
              <AlertCircle className="h-[17px] w-[17px] shrink-0" />
              <p>{status.message}</p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="default"
                size="lg"
                onClick={() => void runScrape({ force: true })}
              >
                <RefreshCw />
                Try again
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onContinue}
              >
                Skip — fill in manually
              </Button>
            </div>
          </div>
        ) : null}

        {status.kind === 'loading' ? <FormSkeleton /> : null}

        {status.kind === 'ready' ? (
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (!validate() || saving) return;
              setSaving(true);
              setSaveError(null);
              void client.onboarding
                .saveBrand({
                  companyName: draft.companyName,
                  description: draft.description,
                  mainColor: draft.mainColor || null,
                  secondaryColor: draft.secondaryColor || null,
                  email: draft.email,
                  phone: draft.phone,
                })
                .then(() => {
                  // New brokerages land in Testing mode, exploring sample
                  // data built from their import; the welcome popup
                  // explains it. If the import failed there is no dataset
                  // to explore, so they land on the live app instead —
                  // clearing any leftover flag from an earlier session
                  // rather than showing fixture data right after we told
                  // them their import failed.
                  setTestMode(demo.kind !== 'failed');
                  onContinue();
                })
                .catch((err: Error) => {
                  setSaving(false);
                  setSaveError(
                    err.message || 'Could not save — try again.',
                  );
                });
            }}
            noValidate
          >
            <div className="auth-row">
              <span className="auth-field-label">Logo &amp; favicon</span>
              <div className="flex items-center gap-4 rounded-md border border-border bg-card px-4 py-3 dark:border-white/10 dark:bg-card-raised">
                <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-background">
                  {draft.logo ? (
                    <img
                      src={draft.logo}
                      alt="Brokerage logo"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <Globe className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-background">
                  {draft.favicon ? (
                    <img
                      src={draft.favicon}
                      alt="Favicon"
                      className="h-full w-full object-contain p-2"
                    />
                  ) : (
                    <Globe className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <p className="flex-1 text-[12.5px] leading-snug text-muted-foreground">
                  Pulled from {prettyHost(draft.sourceUrl) || 'your website'} —
                  swap anytime in settings
                </p>
              </div>
            </div>

            <Row label="Brokerage name" htmlFor="oa-name">
              <Input
                id="oa-name"
                value={draft.companyName ?? ''}
                onChange={(e) => update('companyName', e.target.value)}
                placeholder="e.g. Acme Realty"
              />
            </Row>

            <Row label="One-line description" htmlFor="oa-desc">
              <textarea
                id="oa-desc"
                value={draft.description ?? ''}
                onChange={(e) => update('description', e.target.value)}
                rows={2}
                placeholder="What you're known for, in a sentence."
                className="flex min-h-[68px] w-full resize-y rounded-md border border-border bg-card px-3.5 py-2.5 text-sm transition-colors placeholder:text-muted-foreground/70 focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:border-white/10 dark:bg-card-raised"
              />
            </Row>

            <div className="grid grid-cols-2 gap-4">
              <Row label="Main color" htmlFor="oa-main-color">
                <ColorField
                  id="oa-main-color"
                  value={draft.mainColor}
                  onChange={(v) => update('mainColor', v)}
                />
              </Row>
              <Row label="Secondary color" htmlFor="oa-secondary-color">
                <ColorField
                  id="oa-secondary-color"
                  value={draft.secondaryColor}
                  onChange={(v) => update('secondaryColor', v)}
                />
              </Row>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Row
                label="Contact email"
                htmlFor="oa-email"
                required
                error={errors.email}
              >
                <Input
                  id="oa-email"
                  type="email"
                  required
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'oa-email-error' : undefined}
                  value={draft.email ?? ''}
                  onChange={(e) => update('email', e.target.value || null)}
                  placeholder="hello@…"
                />
              </Row>
              <Row
                label="Phone"
                htmlFor="oa-phone"
                required
                error={errors.phone}
              >
                <Input
                  id="oa-phone"
                  required
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? 'oa-phone-error' : undefined}
                  value={draft.phone ?? ''}
                  onChange={(e) => update('phone', e.target.value || null)}
                  placeholder="+971…"
                />
              </Row>
            </div>

            {/* Locations + Services intentionally not editable here — the
                preview card on the left shows them as pills, and surfacing
                them as form rows added clutter without a clear action. */}

            {saveError && (
              <div className="auth-banner" role="alert">
                <AlertCircle className="h-[17px] w-[17px] shrink-0" />
                <p>{saveError}</p>
              </div>
            )}

            <button
              type="submit"
              className="auth-primary-button auth-login-button"
              disabled={saving}
              aria-busy={saving}
            >
              {saving ? (
                <>
                  <span className="auth-spinner" aria-hidden="true" /> Saving
                </>
              ) : (
                <>
                  Continue <ArrowRight className="h-[17px] w-[17px]" />
                </>
              )}
            </button>
            <p
              style={{
                margin: '14px 0 0',
                textAlign: 'center',
                fontSize: 12.5,
                color: 'var(--auth-muted)',
              }}
            >
              This can be updated later
            </p>
          </form>
        ) : null}
      </>
    </OnboardingShell>
  );
}

function Row({
  label,
  htmlFor,
  required,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="auth-row">
      {/* Plain <label> on purpose: the shared Label component bakes in
          font-mono + uppercase + 0.14em tracking, which reads as shouting
          at the auth screens' 13px label size. */}
      <label htmlFor={htmlFor}>
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        )}
      </label>
      {children}
      {error && (
        <p
          id={`${htmlFor}-error`}
          className="mt-1.5 text-[12.5px] text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  );
}

// Single color picker — native swatch + free-text hex input. The text
// input accepts arbitrary CSS colors (so paste-from-Figma keeps working);
// the swatch syncs only when the value parses as #RRGGBB.
function ColorField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const isHex = /^#[0-9a-fA-F]{6}$/.test(value);
  return (
    <div className="flex h-[50px] items-center gap-2.5 rounded-[14px] border border-[#e3e2f1] bg-white px-2.5 transition-colors focus-within:border-primary/60 dark:border-white/10 dark:bg-card-raised">
      <input
        type="color"
        value={isHex ? value : '#6c5ce7'}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        aria-label="Pick color"
        className="h-8 w-10 shrink-0 cursor-pointer rounded-lg border border-[#e3e2f1] bg-transparent p-0"
      />
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#6C5CE7"
        className="h-full w-full min-w-0 flex-1 bg-transparent text-[14px] uppercase tracking-[0.02em] outline-none placeholder:text-[#9a9ab4]"
      />
    </div>
  );
}

function FormSkeleton() {
  return (
    <div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="auth-row">
          <div className="auth-skeleton auth-skeleton--label" />
          <div className={`auth-skeleton${i === 1 ? ' auth-skeleton--tall' : ''}`} />
        </div>
      ))}
    </div>
  );
}

interface IllustrationProps {
  status: Status;
  website: string | null;
  scrape: DraftScrape;
  demoSlug: string | null;
  /**
   * An agency import is confirmed in flight. Only true for 'pending' —
   * the brief 'resolving' hop falls through to the generic placeholder,
   * since promising test data we might not have would flash the wrong
   * message at anyone on a slow connection.
   */
  demoPending: boolean;
  /** The import is done and produced nothing usable. */
  demoFailed: boolean;
}

function Illustration({
  status,
  website,
  scrape,
  demoSlug,
  demoPending,
  demoFailed,
}: IllustrationProps) {
  const url = scrape.sourceUrl || website || '';
  const host = prettyHost(url);

  return (
    <div className="space-y-3">
      {/* The real public-page renderer, pointed at the demo route
          (apps/landing/app/preview/onboarding-demo) and patched live with
          the new customer's name + logo as they edit the form on the
          right. Listings and team come from their own agency import when
          one landed, and from the fabricated seed set otherwise. */}
      <div className="h-[460px] w-full">
        {demoSlug ? (
          <ProptreePreview
            slug={demoSlug}
            hideChrome
            patch={{
              name: scrape.companyName,
              image: scrape.logo,
              favicon: scrape.favicon,
              // Scraped logos are usually wide wordmarks — contain them
              // in the circular avatar instead of cover-cropping.
              imageFit: 'contain',
            }}
          />
        ) : demoFailed ? (
          <FailedPreview />
        ) : demoPending ? (
          // Deliberately not a skeleton: the wait is for a specific,
          // nameable thing, and saying so beats a shimmer that reads as
          // a page that failed to load.
          <PreparingPreview />
        ) : (
          // Scrape still running — hold the slot so the layout doesn't
          // jump; the iframe mounts already-seeded once we know the
          // branding.
          <div className="h-full w-full animate-pulse rounded-xl border border-border bg-muted/60" />
        )}
      </div>
      {status.kind === 'loading' && (
        <p className="flex items-center gap-2 text-[12px] font-medium text-[#2b2060]">
          <Loader2 className="h-3 w-3 animate-spin" />
          Scanning {host}…
        </p>
      )}
    </div>
  );
}

// Shown while the agency import queued at signup is still running. Same
// footprint as the iframe it replaces, so nothing shifts when it swaps.
function PreparingPreview() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card px-8 text-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      <p className="text-[13.5px] font-medium text-foreground">
        Finishing up your test data
      </p>
      <p className="max-w-[280px] text-[12.5px] leading-snug text-muted-foreground">
        We&rsquo;re pulling your listings and team in so you can try the
        product with your own properties. Carry on filling in the form —
        this finishes on its own.
      </p>
    </div>
  );
}

// Shown when the agency import finished without producing anything we can
// preview. Says what happened rather than quietly swapping in the fabricated
// seed set — the user picked their agency expecting to see their own
// properties, and a silent fixture would misrepresent that.
function FailedPreview() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card px-8 text-center">
      <AlertCircle className="h-5 w-5 text-muted-foreground" />
      <p className="text-[13.5px] font-medium text-foreground">
        Fetching your data failed
      </p>
      <p className="max-w-[280px] text-[12.5px] leading-snug text-muted-foreground">
        We couldn&rsquo;t pull your listings and team in for the preview.
        Nothing is lost — carry on, and you&rsquo;ll go straight to the live
        app where you can add them or connect your CRM.
      </p>
    </div>
  );
}

/**
 * Final iframe URL. Held at null until BOTH the scrape and the dataset
 * have settled, so the frame mounts once with everything it needs — a
 * later `e` would otherwise remount it and flash a second page load.
 */
function previewSlug(demoSlug: string | null, demo: DemoState): string | null {
  if (!demoSlug) return null;
  if (demo.kind === 'resolving' || demo.kind === 'pending') return null;
  // Nothing to preview — the slot renders the failure notice instead.
  if (demo.kind === 'failed') return null;
  if (demo.kind === 'static') return demoSlug;
  return `${demoSlug}${demoSlug.includes('?') ? '&' : '?'}e=${encodeURIComponent(
    demo.enterprise,
  )}`;
}

// The onboarding preview iframes the landing app's static demo route —
// fabricated seed listings only (see apps/landing/app/preview/
// onboarding-demo/page.tsx), NEVER a real agency's proptree. The
// ProptreePreview component just appends this to PUBLIC_URL, so a path
// works fine where a slug normally goes.
const DEMO_PROPTREE_SLUG = 'preview/onboarding-demo';

// Bakes the scraped branding into the demo route's query string so the
// iframe's first server-rendered paint already shows it. Only scrape-
// derived, non-editable-in-form values belong here — live edits travel
// over the patch bridge instead.
function buildDemoSlug(scrape: DraftScrape): string {
  const params = new URLSearchParams();
  if (scrape.companyName) params.set('name', scrape.companyName);
  if (scrape.logo) params.set('logo', scrape.logo);
  if (scrape.favicon) params.set('favicon', scrape.favicon);
  const qs = params.toString();
  return qs ? `${DEMO_PROPTREE_SLUG}?${qs}` : DEMO_PROPTREE_SLUG;
}

function normalize(
  scrape: ScrapedWebsite,
  userEmail: string | null,
): DraftScrape {
  // The wire format gives us fetchedAt as an ISO string; coerce to Date so
  // formatRelative can math on it.
  const fetchedAt =
    scrape.fetchedAt instanceof Date
      ? scrape.fetchedAt
      : new Date(scrape.fetchedAt);
  // Seed the editable Main / Secondary inputs from the top two extracted
  // colors. The user can override either independently.
  const mainColor = scrape.colors[0] ?? '';
  const secondaryColor = scrape.colors[1] ?? '';
  // Prefer the signed-in user's account email — Firecrawl-extracted
  // emails are usually generic mailboxes (info@, contact@) rather than
  // the manager actually onboarding. User can override the field.
  const email = userEmail ?? scrape.email;
  return { ...scrape, fetchedAt, mainColor, secondaryColor, email };
}

function prettyHost(input: string | null): string {
  if (!input) return '';
  try {
    const u = new URL(/^https?:\/\//.test(input) ? input : `https://${input}`);
    return u.host.replace(/^www\./, '');
  } catch {
    return input;
  }
}

